import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { ArrowSquareOut, FileText, PencilSimple } from "@phosphor-icons/react"
import { ListItem, ListText } from "@/components/ui/list"
import { Tag } from "@/components/ui/tag"
import { cn } from "@/lib/utils"
import { ConsultationReportDrawer } from "@/modules/patients/detail/consultation-report/consultation-report-drawer"
import { consultationReportFragment$key } from "@/modules/patients/detail/consultation-report/services/__generated__/consultationReportFragment.graphql"
import { consultationReportFragment } from "@/modules/patients/detail/consultation-report/services/consultationReportFragment"
import { useConsultationReportStoreContext } from "@/modules/patients/detail/consultation-report/store/useConsultationReportStore"
import { DocumentProps } from "@/modules/patients/detail/progress-note/types/document"
import { useConcludeDialogStoreContext } from "@/modules/patients/detail/store/useConcludeDialogStore"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface Props extends DocumentProps {
  fragmentRef: consultationReportFragment$key
  progressNoteId: string
}

const ConsultationReportDocument = ({
  progressNoteId,
  isEditable,
  fragmentRef,
  onSelect,
  isConclusion,
  isSent,
  isSigned,
}: Props) => {
  const { t } = useTranslation()
  const data = useFragment(consultationReportFragment, fragmentRef)
  const setConsultationReportRef = useConsultationReportStoreContext((state) => state.setConsultationReportRef)
  const selectedDocumentId = useConcludeDialogStoreContext((state) => state.selectedDocument?.id)
  const triggeredValidation = useConcludeDialogStoreContext((state) => state.triggeredValidation)
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const { id } = data ?? {}
  const isSelected = isConclusion && selectedDocumentId === id

  if (!id) return null

  const tagLabel = isSigned ? t("patient.conclude.approved") : t("patient.conclude.approveThis")

  return (
    <ListItem
      className={cn(
        "group hover:bg-surface-hovered relative flex h-12 items-center hover:cursor-pointer",
        isSelected && "bg-primary-surface hover:bg-primary-surface-hovered active:bg-primary-surface-pressed",
        !isEditable && !isConclusion && !isSent && "cursor-auto!",
      )}
      data-testid="consultation-report-document-item"
      onClick={() => {
        if (!isEditable) {
          onSelect()
          return
        }
        setConsultationReportRef(fragmentRef)
        openDrawer(() => <ConsultationReportDrawer progressNoteId={progressNoteId} />, "ConsultationReportDrawer")
      }}>
      <FileText className={cn("shrink-0", isSelected ? "text-on-primary-surface" : "text-on-surface-subdued")} />
      <div className="ml-2 min-w-0">
        <ListText className={isSelected ? "text-on-primary-surface" : ""}>
          {t("patient.consultationReport.title")}
        </ListText>
      </div>
      {isEditable && (
        <div className="absolute right-2 ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <PencilSimple className="text-on-surface-subdued" data-testid="consultation-report-edit-button" />
          </div>
        </div>
      )}
      {!isConclusion && isSent && (
        <div className="absolute right-2 ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <ArrowSquareOut
              className="text-on-surface-subdued"
              data-testid="consultation-report-view-document-button"
            />
          </div>
        </div>
      )}
      {isConclusion && (triggeredValidation || isSigned) && (
        <Tag className="ml-auto group-hover:hidden" type={isSigned ? "success" : "critical"} label={tagLabel} />
      )}
    </ListItem>
  )
}

export { ConsultationReportDocument }
